import { useCallback, useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { useSettingsStore } from '@stores/settingsStore';
import { settingsService } from '@services/settings.service';

/** localStorage cache of the choice, so the first paint is right before the profile loads. */
export const NAV_COLLAPSED_KEY = 'co-nav-collapsed';
/** Hover this long before the collapsed rail opens, so passing the mouse over it does nothing. */
export const PEEK_OPEN_DELAY_MS = 200;
/** Grace period after the pointer leaves, so a slight overshoot doesn't snap it shut. */
export const PEEK_CLOSE_DELAY_MS = 120;

function readCached(): boolean {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCached(collapsed: boolean) {
  try {
    localStorage.setItem(NAV_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // Storage blocked (private mode etc.) — the profile still carries the choice.
  }
}

/**
 * The user's own collapsed / expanded choice for the wide-screen rail.
 *
 * The profile (`preferences.sidebarCollapsed`) is the source of truth, so the
 * choice follows the agent to any device; localStorage only covers the first
 * paint. Once the user toggles in this session, a late-arriving profile no
 * longer overrides them.
 */
export function useNavCollapsed() {
  const profilePref = useSettingsStore(
    (s) => (s.profile?.preferences as Record<string, unknown> | undefined)?.sidebarCollapsed,
  );
  const [collapsed, setCollapsed] = useState(readCached);
  const toggledThisSession = useRef(false);

  useEffect(() => {
    if (typeof profilePref !== 'boolean' || toggledThisSession.current) return;
    setCollapsed(profilePref);
    writeCached(profilePref);
  }, [profilePref]);

  const toggle = useCallback(() => {
    const next = !collapsed;
    toggledThisSession.current = true;
    setCollapsed(next);
    writeCached(next);
    // Mirror into the loaded profile so other readers agree, then save. A
    // failed save keeps the local choice; it is retried on the next toggle.
    useSettingsStore.setState((s) =>
      s.profile
        ? { profile: { ...s.profile, preferences: { ...s.profile.preferences, sidebarCollapsed: next } } }
        : {},
    );
    settingsService.updateProfile({ sidebarCollapsed: next }).catch((err) => {
      console.warn('[nav] could not save the sidebar preference', err);
    });
  }, [collapsed]);

  return { collapsed, toggle };
}

function canHover(): boolean {
  return typeof window.matchMedia !== 'function' || window.matchMedia('(hover: hover)').matches;
}

/**
 * Hover / keyboard "peek" for a collapsed rail: the full menu slides out over
 * the page after PEEK_OPEN_DELAY_MS and closes when the pointer or focus
 * leaves, on Escape, or after a nav link is chosen (`close`).
 *
 * Touch devices have no hover, so on them only keyboard focus opens it.
 */
export function useHoverPeek(enabled: boolean) {
  const [peek, setPeek] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const clearTimer = () => {
    if (timer.current !== undefined) {
      window.clearTimeout(timer.current);
      timer.current = undefined;
    }
  };

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      setPeek(false);
    }
  }, [enabled]);

  useEffect(() => clearTimer, []);

  const close = useCallback(() => {
    clearTimer();
    setPeek(false);
  }, []);

  const handlers = {
    onPointerEnter: () => {
      if (!enabled || !canHover()) return;
      clearTimer();
      timer.current = window.setTimeout(() => setPeek(true), PEEK_OPEN_DELAY_MS);
    },
    onPointerLeave: () => {
      clearTimer();
      timer.current = window.setTimeout(() => setPeek(false), PEEK_CLOSE_DELAY_MS);
    },
    onFocus: () => {
      if (enabled) {
        clearTimer();
        setPeek(true);
      }
    },
    onBlur: (e: FocusEvent<HTMLElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close();
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Escape') close();
    },
  };

  return { peek: enabled && peek, close, handlers };
}
